import React, { useEffect, useRef, useState } from "react"
import Paper, { Size, Tool, Path, Color, Point } from "paper"
import { red, lime } from "@ant-design/colors"

export enum DrawingMode {
  ARROW,
  LINE,
  NONE,
}

export const DrawingCanvas: React.FC<{
  className?: string
  width: number
  height: number
  isDrawing?: boolean
  lineWidth: number
  shouldCleanDrawings: boolean
  mode?: DrawingMode
  onDrawingsCleaned: () => void | null
  shouldUndoLastDrawing?: boolean
  onLastDrawingUndone: () => void | null
}> = ({
  className = "",
  width,
  height,
  lineWidth,
  shouldCleanDrawings,
  mode = DrawingMode.NONE,
  onDrawingsCleaned,
  shouldUndoLastDrawing = false,
  onLastDrawingUndone,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  // const [path, setPath] = useState<paper.Path>()
  const [tool, setTool] = useState<paper.Tool>()
  const [currentlyDrawingArrow, setCurrentlyDrawingArrow] =
    useState<paper.Path>()

  const isDrawing = mode !== DrawingMode.NONE

  useEffect(() => {
    if (canvasRef) {
      Paper.setup(canvasRef.current!)
      setTool(new Tool())
    }
  }, [canvasRef])

  useEffect(() => {
    if (Paper.view !== null) {
      Paper.view.viewSize = new Size(width, height)
    }
  }, [width, height])

  useEffect(() => {
    if (Paper.tool) {
      Paper.tool.onMouseDown = (event: { point: paper.PointLike }) => {
        if (isDrawing) {
          const newPath = new Path({
            segments: [event.point],
            strokeColor:
              mode === DrawingMode.ARROW
                ? new Color(`${lime[2]}`)
                : new Color(`${red[3]}`),
            strokeWidth: lineWidth,
            strokeCap: "round",
            miterLimit: 3,
          })
          setCurrentlyDrawingArrow(newPath)
        } else {
          const hitResult = Paper.project.hitTest(event.point, {
            tolerance: 10,
            stroke: true,
          })
          if (hitResult) {
            hitResult.item.selected = true
            setTimeout(() => {
              hitResult.item.remove()
            }, 3000)
          }
        }
      }
      Paper.tool.onMouseDrag = (event: {
        point: number[] | paper.Segment | paper.PointLike
      }) => {
        if (isDrawing) {
          currentlyDrawingArrow?.add(event.point)
        }
      }
      Paper.tool.onMouseUp = (event) => {
        if (isDrawing) {
          currentlyDrawingArrow?.simplify(60)
          if (mode === DrawingMode.ARROW) {
            const lastSegment = currentlyDrawingArrow?.lastSegment

            const arrowVector = lastSegment!.point
              .add(lastSegment!.handleIn)
              .subtract(lastSegment!.point)
              .normalize(16)

            currentlyDrawingArrow?.add(
              arrowVector.rotate(30, new Point(0, 0)).add(lastSegment!.point),
              arrowVector.rotate(-30, new Point(0, 0)).add(lastSegment!.point),
              lastSegment!.point
            )
          }
        }
      }
    }
  }, [currentlyDrawingArrow, isDrawing, mode, lineWidth])

  useEffect(() => {
    if (shouldCleanDrawings) {
      Paper.project.clear()
      onDrawingsCleaned()
    }
  }, [shouldCleanDrawings, onDrawingsCleaned])

  useEffect(() => {
    if (shouldUndoLastDrawing) {
      Paper.project.getItems({ class: Path }).pop()?.remove()
      onLastDrawingUndone()
    }
  }, [shouldUndoLastDrawing, onLastDrawingUndone])

  return (
    <canvas
      className={className}
      ref={canvasRef}
      onMouseDown={(event) => {
        if (!isDrawing) {
          return
        }
      }}
    />
  )
}
